import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DollarSign, UserPlus, Users, TrendingUp } from 'lucide-react';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/analytics/summary`);
        setData(r.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8 text-muted-foreground">Loading analytics...</div>;
  if (!data) return <div className="p-8 text-muted-foreground">No data available.</div>;

  const maxTrend = Math.max(...data.revenue_trend_7d.map(d => d.total), 1);
  const maxSvc = Math.max(...data.top_services_this_week.map(s => s.bookings), 1);

  const stats = [
    { label: "Today's Revenue", val: `$${data.today_revenue.toFixed(2)}`, icon: DollarSign, color: 'text-emerald-500' },
    { label: 'This Month', val: `$${data.month_revenue.toFixed(2)}`, icon: TrendingUp, color: 'text-blue-500' },
    { label: 'New Signups (7d)', val: data.signups_7d, icon: UserPlus, color: 'text-pink-500' },
    { label: 'Total Clients', val: data.total_clients, icon: Users, color: 'text-amber-500' },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Your business at a glance — smarter decisions from real numbers.</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-kpis">
        {stats.map(s => (
          <Card key={s.label} className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</span>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </div>
            <div className="text-3xl font-semibold">{s.val}</div>
          </Card>
        ))}
      </div>

      {/* Revenue trend */}
      <Card className="p-5">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-lg font-semibold">7-Day Revenue Trend</h2>
          <span className="text-xs text-muted-foreground">Last 7 days including today</span>
        </div>
        <div className="flex items-end gap-2 h-40" data-testid="revenue-chart">
          {data.revenue_trend_7d.map((d) => {
            const h = maxTrend > 0 ? (d.total / maxTrend) * 100 : 0;
            const dt = new Date(d.date + 'T00:00:00');
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-2">
                <div className="text-[10px] text-muted-foreground">${d.total.toFixed(0)}</div>
                <div
                  className="w-full rounded-t-md bg-gradient-to-t from-emerald-500 to-emerald-300"
                  style={{ height: `${Math.max(h, 4)}%` }}
                  title={`${d.date}: $${d.total.toFixed(2)}`}
                />
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {dt.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Top services this week */}
      <Card className="p-5">
        <h2 className="text-lg font-semibold mb-4">Top Services This Week</h2>
        {data.top_services_this_week.length === 0 ? (
          <p className="text-sm text-muted-foreground">No appointments this week yet.</p>
        ) : (
          <ul className="space-y-3">
            {data.top_services_this_week.map((s, i) => {
              const w = (s.bookings / maxSvc) * 100;
              return (
                <li key={i} data-testid={`top-service-${i}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{s.service}</span>
                    <span className="text-muted-foreground">{s.bookings} booking{s.bookings === 1 ? '' : 's'}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-pink-400 to-pink-300 rounded-full" style={{ width: `${w}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default Analytics;
